import { useContext, useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import { CONNECTION_STATUS, Context } from "../../redux/Context";
import { GetToken } from "../../utils/helper/helper";

/**
 * How long the live feed may be down before the banner shows. A network blip or the reconnect after
 * a renewal is over in a second or two and is not worth a warning.
 */
export const LIVE_FEED_GRACE_MS = 3000;

/** #182: shown while the session's renewal fails without the server refusing it (apiClient). */
export const RENEWAL_FAILING_MESSAGE = "Your session cannot be renewed – live figures may be out of date.";

/**
 * Tells a signed-in user that live updates have stopped (#182).
 *
 * The bidding screens are push-only: prices, bid counts and countdowns change only when the hub
 * says so. When the connection was down nothing on screen said so - a frozen price looked exactly
 * like a quiet lot, and a player could lose the lot watching it. Rendered by TopNavbar, so it is on
 * every dashboard page; it clears itself once the connection is back, and the live screens reload
 * what they missed (useHubResync).
 *
 * #182: it also says when the session cannot be renewed (no reply to RefreshToken): the feed may
 * still look healthy, but it will stop once the token runs out. That takes priority over the
 * reconnecting text and shows at once - the renewal has already failed, it is no blip.
 *
 * @returns {JSX.Element|null}
 */
const LiveFeedBanner = () => {
  const { connectionStatus, sessionRenewalFailing } = useContext(Context) || {};
  const down =
    connectionStatus === CONNECTION_STATUS.reconnecting ||
    connectionStatus === CONNECTION_STATUS.disconnected;
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!down || !GetToken()) {
      setShow(false);
      return undefined;
    }

    const timer = setTimeout(() => setShow(true), LIVE_FEED_GRACE_MS);
    return () => clearTimeout(timer);
  }, [down]);

  if (sessionRenewalFailing && GetToken()) {
    return (
      <Alert variant="warning" className="live-feed-banner" role="status">
        {RENEWAL_FAILING_MESSAGE}
      </Alert>
    );
  }

  if (!show) return null;

  return (
    <Alert variant="warning" className="live-feed-banner" role="status">
      Live updates paused - reconnecting... Prices and timers on this screen may be out of date.
    </Alert>
  );
};

export default LiveFeedBanner;
